import { useState } from 'react';
import { CarteIncidents } from './CarteIncidents';
import { PiecesJointes } from './PiecesJointes';
import { PrisePhoto, joindrePhoto } from './PrisePhoto';
import type { PhotoReduite } from '../../terrain/photos';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';

interface Mission {
  latitude?: number | null;
  longitude?: number | null;
  incident_reference: string;
  id: string;
  titre: string;
  consignes: string;
  statut: string;
  affectation_id: string;
  version: number;
  localisation: string;
  observations: string;
  travaux: string;
  resultat: string | null;
  commentaire: string;
  consommations: { article_id: string; quantite: number }[];
  participants: string[];
}
function Consommations({
  articles,
}: {
  articles: { id: string; reference: string; libelle: string; unite: string }[];
}) {
  const [lignes, setLignes] = useState<number[]>([]);
  const [prochain, setProchain] = useState(0);
  return (
    <fieldset>
      <legend>Pièces consommées</legend>
      <p>
        Déduites du stock lors de la validation du rapport. Après une demande de correction,
        indiquez de nouveau toutes les consommations.
      </p>
      {lignes.map((id, index) => (
        <div key={id}>
          <label>
            Article {index + 1}{' '}
            <select name="article_id" required>
              <option value="">Choisir</option>
              {articles.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.reference} — {a.libelle} ({a.unite})
                </option>
              ))}
            </select>
          </label>
          <label>
            Quantité consommée {index + 1}{' '}
            <input
              name="quantite_consommee"
              type="number"
              min={1}
              max={1000000}
              step={1}
              required
            />
          </label>
          <button type="button" onClick={() => setLignes(lignes.filter((l) => l !== id))}>
            Retirer la ligne {index + 1}
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={lignes.length >= 50}
        onClick={() => {
          setLignes([...lignes, prochain]);
          setProchain(prochain + 1);
        }}
      >
        Ajouter une consommation
      </button>
    </fieldset>
  );
}
const etats: Record<string, string> = {
  ASSIGNED: 'Affectée',
  ACCEPTED: 'Acceptée',
  IN_PROGRESS: 'En cours',
  BLOCKED: 'Bloquée',
  REPORTED: 'Rapport transmis',
  VALIDATED: 'Validée',
  REJECTED: 'Rapport à corriger',
  CANCELLED: 'Annulée',
};
export function PageInterventionsReseau() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [photos, setPhotos] = useState<Record<string, PhotoReduite | null>>({});
  const [preparationPhoto, setPreparationPhoto] = useState(false);
  const gestion = peut(contexte, 'intervention', 'modifier');
  const equipes = useQuery({
    queryKey: ['equipes-reseau'],
    queryFn: () =>
      requete<{ elements: { id: string; nom: string; active: boolean }[] }>('/equipes-reseau'),
    enabled: creation && gestion,
  });
  const articles = useQuery({
    queryKey: ['interventions-reseau', 'articles'],
    queryFn: () =>
      requete<{ elements: { id: string; reference: string; libelle: string; unite: string }[] }>(
        '/interventions-reseau/articles',
      ),
    enabled: peut(contexte, 'intervention', 'creer'),
  });
  const liste = useQuery({
    queryKey: ['interventions-reseau'],
    queryFn: () => requete<{ elements: Mission[] }>('/interventions-reseau'),
    enabled: peut(contexte, 'intervention', 'voir'),
  });
  const incidents = useQuery({
    queryKey: ['incidents-reseau', 'affectation'],
    queryFn: () =>
      requete<{ elements: { id: string; titre: string; statut: string }[] }>('/incidents'),
    enabled: creation && peut(contexte, 'incident', 'voir'),
  });
  const agents = useQuery({
    queryKey: ['interventions-reseau', 'agents'],
    queryFn: () =>
      requete<{ elements: { id: string; nom: string; prenoms: string | null }[] }>(
        '/interventions-reseau/agents',
      ),
    enabled: creation && gestion,
  });
  const recharger = async () => {
    await client.invalidateQueries({ queryKey: ['interventions-reseau'] });
    await client.invalidateQueries({ queryKey: ['incidents-reseau'] });
    await client.invalidateQueries({ queryKey: ['incident-reseau'] });
    await client.invalidateQueries({ queryKey: ['stocks'] });
  };
  if (!peut(contexte, 'intervention', 'voir'))
    return <p role="alert">Accès aux interventions non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre={gestion ? 'Interventions' : 'Mes missions'} />
      {liste.data && (
        <CarteIncidents
          missions
          incidents={liste.data.elements.map((m) => ({
            id: m.id,
            titre: m.titre,
            reference: m.incident_reference,
            statut: m.statut,
            latitude: m.latitude ?? null,
            longitude: m.longitude ?? null,
          }))}
        />
      )}
      {gestion &&
        peut(contexte, 'incident', 'modifier') &&
        peut(contexte, 'intervention', 'creer') && (
          <button onClick={() => setCreation(!creation)}>
            {creation ? 'Fermer le formulaire' : 'Affecter une intervention'}
          </button>
        )}
      {creation && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 700, marginBlock: 20 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setEnCours(true);
            setErreur('');
            try {
              await requete('/interventions-reseau', {
                methode: 'POST',
                corps: {
                  titre: d.get('titre'),
                  consignes: d.get('consignes'),
                  incident_id: d.get('incident_id'),
                  affectation_id: d.get('affectation_id'),
                  equipe_id: d.get('equipe_id') || null,
                },
              });
              setCreation(false);
              await recharger();
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <label>
            Incident{' '}
            <select name="incident_id" required>
              <option value="">Choisir</option>
              {incidents.data?.elements
                .filter((i) =>
                  [
                    'CONFIRMED',
                    'ASSIGNED',
                    'WAITING_RESOURCES',
                    'REOPENED',
                    'IN_PROGRESS',
                  ].includes(i.statut),
                )
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.titre}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Agent affecté{' '}
            <select name="affectation_id" required>
              <option value="">Choisir</option>
              {agents.data?.elements.map((a) => (
                <option key={a.id} value={a.id}>
                  {[a.prenoms, a.nom].filter(Boolean).join(' ')}
                </option>
              ))}
            </select>
          </label>
          <label>
            Titre de mission <input name="titre" required minLength={5} maxLength={200} />
          </label>
          <label>
            Équipe associée (facultative){' '}
            <select name="equipe_id">
              <option value="">Aucune</option>
              {equipes.data?.elements
                .filter((e) => e.active)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nom}
                  </option>
                ))}
            </select>
          </label>
          {equipes.error && <p role="alert">{messageErreur(equipes.error)}</p>}
          <label>
            Consignes <textarea name="consignes" required minLength={3} maxLength={4000} />
          </label>
          <button disabled={enCours}>Enregistrer l’affectation</button>
          {(agents.error || incidents.error) && (
            <p role="alert">{messageErreur(agents.error || incidents.error)}</p>
          )}
        </form>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      {liste.isPending && <p role="status">Chargement…</p>}
      {liste.error && <p role="alert">{messageErreur(liste.error)}</p>}
      {liste.data && (
        <p>{liste.data.elements.length} intervention(s), au maximum les 200 plus récentes.</p>
      )}
      {liste.data?.elements.map((m) => {
        const mienne =
          m.affectation_id === contexte?.appartenance_active?.id ||
          m.participants.includes(contexte?.appartenance_active?.id ?? '');
        const actions: { code: string; libelle: string }[] = [];
        if (mienne && peut(contexte, 'intervention', 'creer')) {
          if (m.statut === 'ASSIGNED')
            actions.push({ code: 'accepter', libelle: 'Accepter la mission' });
          if (['ACCEPTED', 'BLOCKED', 'REJECTED'].includes(m.statut))
            actions.push({ code: 'commencer', libelle: 'Commencer ou reprendre' });
          if (['ACCEPTED', 'IN_PROGRESS'].includes(m.statut))
            actions.push({ code: 'bloquer', libelle: 'Signaler un blocage' });
          if (m.statut === 'IN_PROGRESS')
            actions.push({ code: 'rapport', libelle: 'Transmettre le rapport' });
        }
        if (
          !mienne &&
          m.statut === 'REPORTED' &&
          peut(contexte, 'intervention', 'valider') &&
          peut(contexte, 'incident', 'valider')
        )
          actions.push(
            { code: 'valider', libelle: 'Valider le rapport' },
            { code: 'rejeter', libelle: 'Demander une correction' },
          );
        if (gestion && ['ASSIGNED', 'ACCEPTED', 'BLOCKED'].includes(m.statut))
          actions.push({ code: 'annuler', libelle: 'Annuler la mission' });
        return (
          <article key={m.id} style={{ borderBottom: '1px solid currentColor', paddingBlock: 20 }}>
            <h2>{m.titre}</h2>
            <PiecesJointes
              type="intervention"
              id={m.id}
              ecriture={
                !['VALIDATED', 'CANCELLED'].includes(m.statut) &&
                (gestion || (mienne && peut(contexte, 'intervention', 'creer')))
              }
            />
            <p>
              {etats[m.statut]} · {m.localisation}
            </p>
            <p>{m.consignes}</p>
            {m.observations && <p>Observations : {m.observations}</p>}
            {m.travaux && <p>Travaux : {m.travaux}</p>}
            {m.resultat && (
              <p>
                Résultat :{' '}
                {
                  (
                    {
                      RESTORED: 'Rétabli',
                      PARTIAL: 'Partiel',
                      TEMPORARY: 'Provisoire',
                      NOT_RESTORED: 'Non rétabli',
                    } as Record<string, string>
                  )[m.resultat]
                }
              </p>
            )}
            {m.commentaire && <p>{m.commentaire}</p>}
            {m.consommations.length > 0 && (
              <ul aria-label="Consommations du rapport">
                {m.consommations.map((p) => (
                  <li key={p.article_id}>
                    {articles.data?.elements.find((a) => a.id === p.article_id)?.libelle ??
                      p.article_id}{' '}
                    : {p.quantite}
                  </li>
                ))}
              </ul>
            )}
            {actions.length > 0 && (
              <form
                key={m.version}
                style={{ display: 'grid', gap: 12, maxWidth: 700 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const d = new FormData(e.currentTarget);
                  setEnCours(true);
                  setErreur('');
                  try {
                    const photo = photos[m.id];
                    if (d.get('action') === 'rapport' && photo) {
                      await joindrePhoto('intervention', m.id, photo);
                      await client.invalidateQueries({
                        queryKey: ['pieces-jointes', 'intervention', m.id],
                      });
                    }
                    await requete(`/interventions-reseau/${m.id}/actions`, {
                      methode: 'POST',
                      corps: {
                        action: d.get('action'),
                        version: m.version,
                        commentaire: d.get('commentaire'),
                        observations: d.get('observations') ?? '',
                        travaux: d.get('travaux') ?? '',
                        consommations:
                          d.get('action') === 'rapport'
                            ? d.getAll('article_id').map((article_id, index) => ({
                                article_id,
                                quantite: Number(d.getAll('quantite_consommee')[index]),
                              }))
                            : [],
                        ...(d.get('resultat') ? { resultat: d.get('resultat') } : {}),
                      },
                    });
                    if (d.get('action') === 'rapport') setPhotos((p) => ({ ...p, [m.id]: null }));
                    await recharger();
                  } catch (err) {
                    setErreur(messageErreur(err));
                  } finally {
                    setEnCours(false);
                  }
                }}
              >
                <label>
                  Action{' '}
                  <select name="action">
                    {actions.map((a) => (
                      <option key={a.code} value={a.code}>
                        {a.libelle}
                      </option>
                    ))}
                  </select>
                </label>
                {actions.some((a) => a.code === 'rapport') && (
                  <>
                    <label>
                      Observations du rapport <textarea name="observations" maxLength={4000} />
                    </label>
                    <label>
                      Travaux réalisés <textarea name="travaux" maxLength={4000} />
                    </label>
                    <label>
                      Résultat{' '}
                      <select name="resultat">
                        <option value="RESTORED">Rétabli</option>
                        <option value="PARTIAL">Partiel</option>
                        <option value="TEMPORARY">Provisoire</option>
                        <option value="NOT_RESTORED">Non rétabli</option>
                      </select>
                    </label>
                    <Consommations articles={articles.data?.elements ?? []} />
                    <PrisePhoto
                      titre="Photo de fin d’intervention"
                      photo={photos[m.id] ?? null}
                      onChange={(photo) => setPhotos((p) => ({ ...p, [m.id]: photo }))}
                      onBusy={setPreparationPhoto}
                      disabled={enCours || preparationPhoto}
                    />
                    {articles.error && <p role="alert">{messageErreur(articles.error)}</p>}
                  </>
                )}
                <label>
                  Commentaire{' '}
                  <textarea name="commentaire" required minLength={3} maxLength={4000} />
                </label>
                <button disabled={enCours || preparationPhoto}>Enregistrer l’action</button>
              </form>
            )}
          </article>
        );
      })}
    </section>
  );
}
